import toast from "react-hot-toast";
import { socketManager } from "../socket-manager";
import { SOCKET_NAME_SPACES, SOCKET_NOTIFICATION_EVENTS } from "../socket.enum";
import type {
  IWebSocketSinglePushNotification,
  ISingleNotification,
  IWebSocketNotificationConnected,
} from "@/types/notifications.interface";
import { notificationsApi } from "@/services/notificationsApi";
import { store } from "@/store";
import { NOTIFICATIONS_PLATFORM_FILTER_ENUM } from "@/utils/enums/notifications.enum";
import { setAnnouncementId, setUnreadAnnouncementCount, setUnreadNotificationsCount } from "@/store/slices/user.slice";
import { subscriptionApi } from "@/services/(company)/subscriptionApi";
import { userApi } from "@/services/(my-space)/userApi";
import { companyBranchesApi } from "@/services/(company)/branchesApi";
import { playNotificationSound, speak } from "@/utils/notifications.util";
import type { ISingleAnnouncement, IWebSocketSinglePushAnnouncement } from "@/types/announcement.interface";
import { announcementApi } from "@/services/api.announcements";
import { extractAnnouncementPreview, playAnnouncementSound } from "@/utils/announcement.util";
import { ANNOUNCEMENTS_PLATFORM_FILTER_ENUM } from "@/utils/enums/announcement.enum";

const sanitizeToPlainText = (html: string): string =>
  html
    .replace(/&nbsp;/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/<[^>]*>/g, "")
    .trim();

const showBrowserNotification = (title: string, body: string, tag: string) => {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  const notification = new Notification(title, {
    body,
    icon: "/favicon.ico",
    tag,
  });

  notification.onclick = () => {
    window.focus();
    notification.close();
  };
};

// Helper to check if a notification should be added to a specific query cache
const shouldUpdateCache = (originalArgs: any, notification: ISingleNotification) => {
  const { platformSectionType: tab } = originalArgs;
  const { ALL, UNREAD } = NOTIFICATIONS_PLATFORM_FILTER_ENUM;

  // 1. Basic matching: All, Unread, or specific category match
  const tabMatches = tab === ALL || tab === UNREAD || tab === notification.platformSectionType;
  // 2. If it's the 'Unread' tab, only add if the notification is actually unread
  const unreadCondition = tab === UNREAD ? !notification.isRead : true;

  return tabMatches && unreadCondition;
};

// Helper to check if a announcement should be added to a specific query cache
const shouldAnnouncementUpdateCache = (originalArgs: any, announcement: ISingleAnnouncement) => {
  const { platformSectionType: tab } = originalArgs;
  const { ALL, UNREAD } = ANNOUNCEMENTS_PLATFORM_FILTER_ENUM;

  // 1. Basic matching: All, Unread, or specific category match
  const tabMatches = tab === ALL || tab === UNREAD || tab === announcement.platformSectionType;
  // 2. If it's the 'Unread' tab, only add if the notification is actually unread
  const unreadCondition = tab === UNREAD ? !announcement.isRead : true;

  return tabMatches && unreadCondition;
};

//  Common logic to mutate the notification list
const updateNotificationList = (data: any, notification: ISingleNotification, limit: number) => {
  const list = data.notificationData;
  if (list.some((n: any) => n.id === notification.id)) return;

  list.unshift(notification);
  data.unreadCount += 1;

  if (list.length > limit) list.pop();
};

const updateAnnouncementList = (data: any, announcement: ISingleAnnouncement, limit: number) => {
  const list = data.announcementData;
  if (list.some((n: any) => n.id === announcement.id)) return;

  list.unshift(announcement);
  data.unreadCount += 1;

  if (list.length > limit) list.pop();
};

export const initNotificationListeners = () => {
  // Notificaitons socket connected
  socketManager.on(
    SOCKET_NAME_SPACES.NOTIFICATIONS,
    SOCKET_NOTIFICATION_EVENTS.CONNECTED,
    (payload: IWebSocketNotificationConnected) => {
      store.dispatch(setUnreadNotificationsCount(payload.unreadCountNotification));
      store.dispatch(setUnreadAnnouncementCount(payload.unreadCountAnnouncement));
    },
  );

  // New Notificaitons
  socketManager.on(
    SOCKET_NAME_SPACES.NOTIFICATIONS,
    SOCKET_NOTIFICATION_EVENTS.NEW_NOTIFICATION,
    (payload: IWebSocketSinglePushNotification, event: string) => {
      const state = store.getState();
      const newNotif = payload.notificationData;

      // --- 1. Update Normal Queries (Pagination) ---
      const normalQueries = notificationsApi.util.selectInvalidatedBy(state, [{ type: "getAllNotifications" }]);

      normalQueries.forEach(({ originalArgs }) => {
        if (originalArgs?.page === 1 && shouldUpdateCache(originalArgs, newNotif)) {
          store.dispatch(
            notificationsApi.util.updateQueryData("getAllNotifications", originalArgs, (draft) => {
              updateNotificationList(draft.data, newNotif, originalArgs.limit || 10);
            }),
          );
        }
      });

      // --- 2. Update Infinite Queries ---
      const infiniteQueries = notificationsApi.util.selectInvalidatedBy(state, [{ type: "getNotifications" }]);

      infiniteQueries.forEach(({ originalArgs }) => {
        if (shouldUpdateCache(originalArgs, newNotif)) {
          store.dispatch(
            notificationsApi.util.updateQueryData("getNotifications", originalArgs, (draft) => {
              if (draft.pages?.[0]) {
                updateNotificationList(draft.pages[0].data, newNotif, originalArgs.limit || 10);
              }
            }),
          );
        }
      });
      store.dispatch(setUnreadNotificationsCount(payload.unreadCount));
      playNotificationSound();

      // Read the notification aloud if the user enabled "Notification read out loud".
      if (state.user.notificationSettings.isTextToSpeechNotificationEnabled) {
        speak(sanitizeToPlainText(newNotif.message));
      }

      toast.success(newNotif.message, { id: event });

      // Browser/OS level notification
      showBrowserNotification(
      newNotif.type,
      sanitizeToPlainText(newNotif.message),
      String(newNotif.id),
    );
    },
  );

  //New Announcement Notifications
  socketManager.on(
    SOCKET_NAME_SPACES.NOTIFICATIONS,
    SOCKET_NOTIFICATION_EVENTS.NEW_ANNOUNCEMENT_NOTIFICAITON,
    (payload: IWebSocketSinglePushAnnouncement, event: string) => {
      const state = store.getState();
      const newAnnouncement = payload.announcementData;

      // --- 1. Update Normal Queries (Pagination) ---
      const normalQueries = announcementApi.util.selectInvalidatedBy(state, [{ type: "getAllAnnouncements" }]);

      normalQueries.forEach(({ originalArgs }) => {
        if (originalArgs?.page === 1 && shouldAnnouncementUpdateCache(originalArgs, newAnnouncement)) {
          store.dispatch(
            announcementApi.util.updateQueryData("getAllAnnouncements", originalArgs, (draft) => {
              updateAnnouncementList(draft.data, newAnnouncement, originalArgs.limit || 10);
            }),
          );
        }
      });

      // --- 2. Update Infinite Queries ---
      const infiniteQueries = announcementApi.util.selectInvalidatedBy(state, [{ type: "getAnnouncement" }]);

      infiniteQueries.forEach(({ originalArgs }) => {
        if (shouldAnnouncementUpdateCache(originalArgs, newAnnouncement)) {
          store.dispatch(
            announcementApi.util.updateQueryData("getAnnouncement", originalArgs, (draft) => {
              if (draft.pages?.[0]) {
                updateAnnouncementList(draft.pages[0].data, newAnnouncement, originalArgs.limit || 10);
              }
            }),
          );
        }
      });
      store.dispatch(setUnreadAnnouncementCount(payload.unreadCount));
      store.dispatch(setAnnouncementId(newAnnouncement.id));
      playAnnouncementSound();
      const message = extractAnnouncementPreview(newAnnouncement.message);

      // Read the announcement aloud if the user enabled "Notification read out loud".
      if (state.user.notificationSettings.isTextToSpeechNotificationEnabled) {
        speak(message);
      }

      toast.success(message, {
        id: event,
      });

      // Browser/OS level notification
      showBrowserNotification(
      newAnnouncement.type,
      sanitizeToPlainText(newAnnouncement.message),
      String(newAnnouncement.id),
    );
    },
  );

  // Token balance updated (AI action consumed tokens) — refetch header token count + time saved
  socketManager.on(
    SOCKET_NAME_SPACES.NOTIFICATIONS,
    SOCKET_NOTIFICATION_EVENTS.TOKEN_BALANCE_UPDATED,
    () => {
      store.dispatch(
        subscriptionApi.util.invalidateTags(["getTokenUsage", "getMyTimeSaved"]),
      );
    },
  );

  // Permissions/scope changed server-side (admin edited this user's role) —
  // refetch the sidebar menu + accessible branches so access updates live,
  // without a re-login or hard refresh.
  socketManager.on(
    SOCKET_NAME_SPACES.NOTIFICATIONS,
    SOCKET_NOTIFICATION_EVENTS.PERMISSIONS_CHANGED,
    () => {
      store.dispatch(userApi.util.invalidateTags(["getSidebarPersmissions"]));
      store.dispatch(
        companyBranchesApi.util.invalidateTags(["getBranches"]),
      );
    },
  );
};
